import { z } from 'zod';
/**
 * Doc 17 — time scope + status filter shared by every "my shifts" list
 * (facility + homecare Booked shifts, care professional Schedule).
 *
 * One time rule for the whole platform (A1): a shift is **upcoming** until its
 * scheduled end, **past** after it. Status never moves a shift between the two
 * — a cancelled future shift stays upcoming with its Cancelled badge.
 *
 * `range` matches shifts whose scheduled START is in [from, to). The portal
 * sends `from` = the viewer's local midnight of the first day and `to` = local
 * midnight of the day after the last day (A3).
 */
export const SHIFT_LIST_SCOPES = ['upcoming', 'past', 'range'];
/**
 * Status filter values. `NEEDS_SIGNOFF` is derived, not stored (A5): the
 * scheduled end has passed and the timesheet is unsigned (status still
 * SCHEDULED / IN_PROGRESS). `SCHEDULED` therefore means scheduled and not yet
 * ended. IN_PROGRESS is left as-is (doc 17 "out of scope").
 */
export const SHIFT_LIST_STATUS_FILTERS = [
    'SCHEDULED',
    'NEEDS_SIGNOFF',
    'IN_PROGRESS',
    'COMPLETED',
    'NO_SHOW',
    'CANCELLED',
    'DISPUTED',
];
export const ShiftListQuerySchema = z
    .object({
    scope: z.enum(SHIFT_LIST_SCOPES).default('upcoming'),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    status: z.enum(SHIFT_LIST_STATUS_FILTERS).optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
})
    .superRefine((q, ctx) => {
    if (q.scope !== 'range')
        return;
    if (!q.from || !q.to) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [q.from ? 'to' : 'from'],
            message: 'A date range needs both a start and an end date.',
        });
        return;
    }
    // `to` is exclusive (the portal sends the day after the chosen end date).
    if (new Date(q.to).getTime() <= new Date(q.from).getTime()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: 'The end must be after the start.',
        });
    }
});
/** Statuses at which a shift still awaits the buyer's timesheet sign-off. */
const UNSIGNED_STATUSES = ['SCHEDULED', 'IN_PROGRESS'];
/**
 * The status to show on a shift row: the stored status, except an unsigned
 * shift whose scheduled end has passed shows `NEEDS_SIGNOFF`. Used by the
 * portal badge and the admin shifts card. The backend filters
 * (`shiftListWhere`, admin `statusGroupWhere`) express the same rule as a
 * Prisma where; `shift-list-filter.spec.ts` pins the two together.
 */
export function shiftDisplayStatus(shift, now = new Date()) {
    if (UNSIGNED_STATUSES.includes(shift.status) && new Date(shift.scheduledEnd).getTime() < now.getTime()) {
        return 'NEEDS_SIGNOFF';
    }
    return shift.status;
}
//# sourceMappingURL=list-scope.schema.js.map